import { useWindowSize } from "@hooks/useWindowSize";
import {
  getCardLayout,
  getTopStoriesCardPostion,
} from "@pages/Global/utils/helpers";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { SanitizedSectionArticle } from "@typings/Cue";
import { cn } from "@util/helpers";

type TopStoriesSectionProps = {
  /** The articles to be displayed in the section */
  articles: SanitizedSectionArticle[];
  /** Optional className for the section */
  className?: string;
  /** Optional link params */
  linkParams?: URLSearchParams;
};

/**
 * TopStoriesSection component to display a list of top stories articles.
 * It renders a section with a title and a list of articles as cards.
 */
const TopStoriesSection = ({
  articles,
  className,
  linkParams,
}: TopStoriesSectionProps): React.ReactElement => {
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);

  const { isScreenLG } = useWindowSize();

  if (!articles.length) return <></>;

  return (
    <section
      data-testid="global-top-stories-section-component"
      className={cn(
        "mb-10 flex flex-col",
        "grid-flow-col grid-cols-8 grid-rows-4 lg:grid",
        className
      )}
    >
      <>
        {articles.map((article, index) => {
          const CardLayout = getCardLayout(index);
          const cardPosition = getTopStoriesCardPostion(index);

          const isLeadArticle = index === 0;

          const isNextTwoArticles = index === 1 || index === 2;
          const isDisplayedImage = !isScreenLG && isNextTwoArticles;

          return (
            <CardLayout
              key={index}
              article={article}
              displayBlurb={index === 0}
              displayEdited={false}
              displayUpdated={index === 0}
              displaySection
              contentClassName="px-0"
              imageClassName={cn({
                "w-svw -mx-3 md:w-full md:mx-0": isLeadArticle,
              })}
              className={cn(
                "relative my-4",
                "after:absolute after:-bottom-4 after:left-0 after:w-full after:border-b after:border-gray-175",
                { block: isLeadArticle },
                { "text-center lg:mx-6": isLeadArticle },
                { "mt-0": index === 0 },
                { "mt-4 lg:mt-0": index === 1 || index === 2 },
                {
                  "after:lg:hidden":
                    index === 0 || index === articles.length - 2,
                },
                {
                  "after:hidden": index === articles.length - 1,
                },
                cardPosition
              )}
              linkParams={linkParams}
              displayImage={isDisplayedImage}
              disablePaywall={
                OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER
              }
            />
          );
        })}
      </>
    </section>
  );
};

export default TopStoriesSection;
